import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth, useToast } from "../App";
import { api } from "../lib/api";
import type { ChatMessage } from "../lib/types";

const SUGGESTIONS = [
  "Make these harder",
  "Only keep cards likely to be on an exam",
  "Turn the definition cards into cloze",
  "Split any card that tests more than one thing",
  "Remove anything that isn't worth memorising",
];

export default function ChatPanel({
  projectId,
  selectedIds,
  onApplied,
}: {
  projectId: string;
  selectedIds: string[];
  onApplied: (changedIds: string[]) => void;
}) {
  const { pushError } = useToast();
  const { auth } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);
  const chatbotEnabled = auth?.limits.chatbot_enabled ?? false;

  useEffect(() => {
    api.chatHistory(projectId).then(setMessages).catch(() => {});
  }, [projectId]);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text: string) => {
    const message = text.trim();
    if (!message || busy) return;
    setBusy(true);
    setInput("");
    const optimistic: ChatMessage = {
      id: `local-${Date.now()}`,
      role: "user",
      content: message,
      actions: [],
      created_at: new Date().toISOString(),
    };
    setMessages((current) => [...current, optimistic]);

    try {
      const result = await api.chat(projectId, message, selectedIds.length ? selectedIds : undefined);
      setMessages((current) => [
        ...current,
        {
          id: `local-r-${Date.now()}`,
          role: "assistant",
          content: result.reply,
          actions: result.actions,
          created_at: new Date().toISOString(),
        },
      ]);
      onApplied(result.changed_card_ids);
    } catch (error) {
      pushError(error);
      setMessages((current) => current.filter((m) => m.id !== optimistic.id));
      setInput(message);
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    try {
      await api.clearChat(projectId);
      setMessages([]);
    } catch (error) {
      pushError(error);
    }
  };

  return (
    <div className="panel chat-panel">
      <div className="row">
        <h3 className="grow">Refine with AI</h3>
        {messages.length > 0 && (
          <button className="btn-ghost btn-sm" onClick={clear}>
            Clear
          </button>
        )}
      </div>

      {!chatbotEnabled ? (
        <div className="empty" style={{ padding: "24px 16px" }}>
          <h3 style={{ marginBottom: 4 }}>AI refinement is a Pro feature</h3>
          <p className="muted small" style={{ marginBottom: 12 }}>
            "Make these harder." "Turn these into cloze." Talk to the assistant and it edits
            your cards directly — Pro only.
          </p>
          <Link to="/pricing" className="btn btn-primary btn-sm">
            See plans
          </Link>
        </div>
      ) : (
        <>
          <p className="small faint" style={{ margin: 0 }}>
            {selectedIds.length > 0
              ? `Changes will apply to the ${selectedIds.length} selected card${
                  selectedIds.length === 1 ? "" : "s"
                }.`
              : "Changes apply to the whole deck. Select cards first to narrow the scope."}
          </p>

          <div className="chat-log" ref={logRef}>
            {messages.length === 0 && (
              <div className="row row-wrap" style={{ gap: 5 }}>
                {SUGGESTIONS.map((suggestion) => (
                  <button key={suggestion} className="suggestion" onClick={() => send(suggestion)}>
                    {suggestion}
                  </button>
                ))}
              </div>
            )}

            {messages.map((message) => (
              <div key={message.id} className={`chat-msg ${message.role}`}>
                {message.content}
                {message.actions?.length > 0 && (
                  <div className="chat-actions">
                    {summarise(message.actions).map((label, i) => (
                      <span key={i} className="chip">
                        {label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {busy && (
              <div className="chat-msg assistant">
                <span className="spinner" /> Working through the cards…
              </div>
            )}
          </div>

          <div className="row">
            <input
              type="text"
              className="grow"
              placeholder="Ask for a change…"
              value={input}
              disabled={busy}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") send(input);
              }}
            />
            <button className="btn-primary" onClick={() => send(input)} disabled={busy || !input.trim()}>
              Send
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** Roll a list of operations into a few readable chips. */
function summarise(actions: Record<string, any>[]): string[] {
  const counts = new Map<string, number>();
  let failed = 0;
  for (const action of actions) {
    if (action.error) {
      failed += 1;
      continue;
    }
    const key = String(action.op ?? "changed");
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const labels: Record<string, string> = {
    edit: "edited",
    convert: "converted",
    accept: "accepted",
    reject: "rejected",
    flag: "flagged",
    merge: "merged",
    split: "split",
    retag: "retagged",
  };
  const out = [...counts.entries()].map(([op, n]) => `${n} ${labels[op] ?? op}`);
  if (failed) out.push(`${failed} could not be applied`);
  return out;
}
